import Phaser from 'phaser'
import { GAME_WIDTH, GAME_HEIGHT } from '../../config'
import { ScoreDisplay } from '../../ui/ScoreDisplay'
import { TimerDisplay } from '../../ui/TimerDisplay'
import { ProgressDisplay } from '../../ui/ProgressDisplay'
import { calculateStars, type StarRating } from '../../utils/scoring'
import { showScorePopup, showFeedbackText } from '../../utils/feedback'

export interface MiniGameConfig {
  maxScore: number
  duration?: number // in seconds, undefined = no timer
  totalItems?: number // for progress tracking
}

export interface MiniGameResult {
  score: number
  maxScore: number
  stars: number
  label: string
  sceneKey: string
  nextScene?: string
}

/**
 * Base class for all mini-games
 * Provides common UI, scoring, and lifecycle management
 */
export abstract class BaseMiniGameScene extends Phaser.Scene {
  protected config!: MiniGameConfig
  protected scoreDisplay!: ScoreDisplay
  protected timerDisplay?: TimerDisplay
  protected progressDisplay?: ProgressDisplay
  protected score: number = 0
  protected isPaused: boolean = false
  protected isComplete: boolean = false

  // Subclasses must implement these
  abstract getConfig(): MiniGameConfig
  abstract setupGame(): void
  abstract getNextScene(): string | undefined

  create(): void {
    this.score = 0
    this.isPaused = false
    this.isComplete = false
    this.config = this.getConfig()

    this.cameras.main.fadeIn(300)

    this.createBackground()
    this.createUI()
    this.setupGame()

    // Start timer if configured
    if (this.timerDisplay) {
      this.timerDisplay.start()
    }
  }

  /**
   * Override in subclass to customize background
   */
  protected createBackground(): void {
    // Default: simple gradient background
    const graphics = this.add.graphics()
    graphics.fillStyle(0x2d1b4e)
    graphics.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT)
  }

  /**
   * Create standard UI elements
   */
  protected createUI(): void {
    // Score display (top right)
    this.scoreDisplay = new ScoreDisplay(this, {
      x: GAME_WIDTH - 90,
      y: 35,
      label: 'Score',
      initialScore: 0,
    })

    // Timer display (top left) - if duration is set
    if (this.config.duration) {
      this.timerDisplay = new TimerDisplay(this, {
        x: 70,
        y: 35,
        duration: this.config.duration,
        onComplete: () => this.onTimeUp(),
      })
    }

    // Progress display (top center) - if totalItems is set
    if (this.config.totalItems) {
      this.progressDisplay = new ProgressDisplay(this, {
        x: GAME_WIDTH / 2,
        y: 35,
        total: this.config.totalItems,
      })
    }

    // Pause button
    this.createPauseButton()

    // Back button
    this.createBackButton()
  }

  private createPauseButton(): void {
    const pauseBtn = this.add.text(GAME_WIDTH / 2, 35, '⏸', {
      fontSize: '28px',
      backgroundColor: '#00000066',
      padding: { x: 8, y: 4 },
    })
    pauseBtn.setOrigin(0.5)
    pauseBtn.setInteractive({ useHandCursor: true })
    pauseBtn.setVisible(false) // Hide for now, implement pause menu later

    pauseBtn.on('pointerup', () => this.togglePause())
  }

  private createBackButton(): void {
    const backBtn = this.add.text(20, GAME_HEIGHT - 40, '← Back', {
      fontFamily: 'system-ui',
      fontSize: '18px',
      color: '#ffffff',
      backgroundColor: '#00000066',
      padding: { x: 10, y: 5 },
    })
    backBtn.setInteractive({ useHandCursor: true })
    backBtn.on('pointerover', () => backBtn.setAlpha(0.8))
    backBtn.on('pointerout', () => backBtn.setAlpha(1))
    backBtn.on('pointerup', () => this.exitToMenu())
  }

  /**
   * Add points to the score
   */
  protected addScore(points: number, x?: number, y?: number): void {
    this.score += points
    this.scoreDisplay.setScore(this.score)

    // Show floating score popup if position provided
    if (x !== undefined && y !== undefined) {
      showScorePopup(this, {
        x,
        y,
        points,
        color: points > 0 ? 0x4caf50 : 0xff4444,
      })
    }
  }

  /**
   * Update progress counter
   */
  protected incrementProgress(): void {
    if (this.progressDisplay) {
      this.progressDisplay.increment()

      if (this.progressDisplay.isComplete()) {
        this.onAllItemsComplete()
      }
    }
  }

  /**
   * Show feedback text (Perfect!, Great!, etc.)
   */
  protected showFeedback(text: string, x: number, y: number, color?: number): void {
    showFeedbackText(this, x, y, text, color)
  }

  /**
   * Called when timer runs out
   */
  protected onTimeUp(): void {
    this.completeGame()
  }

  /**
   * Called when all items are processed (for progress-based games)
   */
  protected onAllItemsComplete(): void {
    this.completeGame()
  }

  /**
   * Mark game as complete and show results
   */
  protected completeGame(): void {
    if (this.isComplete) return
    this.isComplete = true

    // Stop timer if running
    this.timerDisplay?.stop()

    // Calculate stars
    const rating = calculateStars(this.score, this.config.maxScore)

    // Prepare result data
    const result: MiniGameResult = {
      score: this.score,
      maxScore: this.config.maxScore,
      stars: rating.stars,
      label: rating.label,
      sceneKey: this.scene.key,
      nextScene: this.getNextScene(),
    }

    // Transition to complete scene
    this.time.delayedCall(500, () => {
      this.cameras.main.fadeOut(300)
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('MiniGameCompleteScene', result)
      })
    })
  }

  /**
   * Toggle pause state
   */
  protected togglePause(): void {
    this.isPaused = !this.isPaused

    if (this.isPaused) {
      this.timerDisplay?.pause()
      this.scene.pause()
      // TODO: Show pause menu overlay
    } else {
      this.timerDisplay?.resume()
      this.scene.resume()
    }
  }

  /**
   * Exit back to main menu
   */
  protected exitToMenu(): void {
    this.timerDisplay?.stop()
    this.cameras.main.fadeOut(300)
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('MainMenuScene')
    })
  }

  /**
   * Get current score
   */
  getScore(): number {
    return this.score
  }

  /**
   * Get star rating for current score
   */
  getStarRating(): StarRating {
    return calculateStars(this.score, this.config.maxScore)
  }
}
